import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Button, IconButton, Menu, MenuItem, TextField } from '@mui/material';
import './RefundDetails.css';
import Layout from '../../../../../components/Layout';
import AdminNavbar from '../../../../../components/Admin/AdminNavbar/AdminNavbar';
import { useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { formatDateFull, formatDateTime } from '../../../../../utils/dateUtils';
import { toast } from 'react-hot-toast';
import RefundDetailsSkeleton from '../../../../../components/SkeletonEffect/RefundDetailsSkeleton/RefundDetailsSkeleton';
import RefundApproveModal from '../../../../../components/Admin/Payments/Refund/RefundApproveModal/RefundApproveModal';
import RefundRejectModal from '../../../../../components/Admin/Payments/Refund/RefundRejectModal/RefundRejectModal';
import { More, ArrowSquareLeft } from 'iconsax-react';
import { useProfile } from '../../../../../context/ProfileContext';

const UserRoleColor = {
  Learner: { color: '#6B60E7', bgcolor: '#F0EFFF', value: 'Learner' },
  Instructor: { color: '#BC8D00', bgcolor: '#FFF3CE', value: 'Instructor' },
};

const StatusColor = {
  pending: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Pending' },
  completed: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Completed' },
  rejected: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Rejected' },
  approved: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Approved' },
  processing: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Processing' },
  failed: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Failed' },
};

const RefundDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile } = useProfile();

  const [loading, setLoading] = useState(false);
  const [refundDetails, setRefundDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [note, setNote] = useState('');

  const adminName = profile
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Admin'
    : 'Admin';

  const getRoleDisplayName = () => {
    if (!profile || !Array.isArray(profile.roles) || profile.roles.length === 0) {
      return 'Admin';
    }
    const role = profile.roles[0];
    const roleMap = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      operations_officer: 'Operations Officer',
      organisation_manager: 'Operations Officer',
      user_onboarding: 'Talent Manager',
      talent_manager: 'Talent Manager',
      finance_officer: 'Finance Officer',
      compliance_auditor: 'Compliance Auditor',
      support_associate: 'Support Associate',
    };
    return roleMap[role] || 'Admin';
  };

  const adminNameWithRole = `${adminName} - ${getRoleDisplayName()}`;

  const getProcessedByRoleDisplayName = role => {
    if (!role) return null;
    const roleMap = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      operations_officer: 'Operations Officer',
      organisation_manager: 'Operations Officer',
      user_onboarding: 'Talent Manager',
      talent_manager: 'Talent Manager',
      finance_officer: 'Finance Officer',
      compliance_auditor: 'Compliance Auditor',
      support_associate: 'Support Associate',
    };
    return roleMap[role] || role;
  };

  const formatProcessedByNameWithRole = (name, role) => {
    if (!name) return 'Admin';
    if (role) {
      const roleDisplay = getProcessedByRoleDisplayName(role);
      return `${name} - ${roleDisplay}`;
    }
    return name;
  };

  const formatPaymentMethod = method => {
    const methodMap = {
      apple_pay: 'Apple Pay',
      qpay: 'QPay',
      qpay_ez_connect: 'QPay (EZ Connect)',
      cybs: 'Cybersource',
      cybersource: 'Cybersource',
      cybersource_card: 'Cybersource Card',
    };
    return methodMap[method?.toLowerCase()] || method || '-';
  };

  const getPaymentGatewayFields = details => {
    if (!details) return {};
    const method = details.payment_method?.toLowerCase();

    switch (method) {
      case 'apple_pay':
        return {
          referenceNumber: details.qpay_apple_pay_reference_number || '-',
          confirmationId: details.qpay_apple_pay_order_number || '-',
          additionalRef: details.qpay_reference_number || null,
        };

      case 'qpay':
      case 'qpay_ez_connect':
        return {
          referenceNumber: details.qpay_reference_number || '-',
          confirmationId: details.qpay_confirmation_id || '-',
        };

      case 'cybs':
      case 'cybersource':
      case 'cybersource_card':
        return {
          transactionId: details.cybs_transaction_id || '-',
        };

      default:
        return {
          referenceNumber: '-',
          confirmationId: '-',
        };
    }
  };

  const fetchRefundDetails = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axiosInstance.get(`/admin/students/refund-requests/${id}`);
      const rec = response.data;

      const mapped = {
        refundId: rec.id,
        paymentId: rec.payment_id,
        studentId: rec.student_id,
        userId: rec.user_details?.id,
        userRoleSpecificId: rec.student_id,
        userName:
          `${rec.user_details?.first_name || ''} ${rec.user_details?.last_name || ''}`.trim() ||
          'Unknown',
        userRole:
          rec.user_details?.user_role?.toLowerCase() === 'student'
            ? 'Learner'
            : rec.user_details?.user_role?.toLowerCase() === 'teacher'
              ? 'Instructor'
              : rec.user_details?.user_role || 'Learner',
        phoneNumber: rec.user_details?.phone_number || '-',
        email: rec.user_details?.email || '-',
        currentLocation:
          `${rec.user_details?.city || ''}, ${rec.user_details?.country || ''}`.trim() || '-',
        dateOfJoining: rec.user_details?.date_of_joining,
        refundReason: rec.reason || '-',
        rejectionReason: rec.rejection_reason,
        adminNotes: rec.admin_notes,
        paymentGatewayDetails: rec.payment_gateway_details,
        paymentMethod: formatPaymentMethod(rec.payment_gateway_details?.payment_method),
        paymentMethodRaw: rec.payment_gateway_details?.payment_method,
        paymentStatus: rec.payment_gateway_details?.payment_status
          ? rec.payment_gateway_details.payment_status.charAt(0).toUpperCase() +
            rec.payment_gateway_details.payment_status.slice(1).toLowerCase()
          : '-',
        timestamp: rec.payment_gateway_details?.created_at,
        gatewayFields: getPaymentGatewayFields(rec.payment_gateway_details),
        purchasedKoins: rec.purchased_koins || 0,
        purchasedAmount: rec.purchased_amount || 0,
        consumedKoins: rec.consumed_koins || 0,
        consumedAmount: rec.consumed_amount || 0,
        refundKoins: rec.refund_koins || 0,
        refundAmount: rec.refund_amount || 0,
        status: rec.status?.toLowerCase() || 'pending',
        processedBy: rec.processed_by,
        processedByRole: rec.processed_by_user_role,
        processedAt: rec.processed_at,
        createdAt: rec.updated_at || rec.created_at,
      };

      setRefundDetails(mapped);
    } catch (err) {
      console.error('Error fetching refund details:', err);
      if (err.response?.status === 404) {
        toast.error('Refund not found');
        setRefundDetails(null);
      } else {
        toast.error('Failed to load refund details');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRefundDetails();
  }, [fetchRefundDetails]);

  const handleApprove = async (adminNotes = '') => {
    setActionLoading(true);
    try {
      const response = await axiosInstance.post(`/admin/students/refund-requests/${id}/approve`, {
        admin_notes: adminNotes || '',
      });

      const status = response.data?.status?.toLowerCase();

      if (status === 'completed') {
        toast.success('Refund approved and processed successfully');
      } else if (status === 'failed') {
        toast.error('Refund approved but payment processing failed');
      } else {
        toast.success('Refund approved successfully');
      }

      fetchRefundDetails();
      setApproveModalOpen(false);
    } catch (error) {
      console.error('Error approving refund:', error);
      toast.error(error.response?.data?.message || 'Failed to approve refund');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async reason => {
    setActionLoading(true);
    try {
      await axiosInstance.post(`/admin/students/refund-requests/${id}/reject`, {
        rejected_reason: reason,
        admin_notes: '',
      });
      toast.success('Refund rejected successfully');
      fetchRefundDetails();
    } catch (error) {
      console.error('Error rejecting refund:', error);
      toast.error(error.response?.data?.message || 'Failed to reject refund');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Refund Details">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <RefundDetailsSkeleton />
          </Box>
        </Box>
      </Layout>
    );
  }

  if (!refundDetails) {
    return (
      <Layout title="Refund Details">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <AdminNavbar />
          </Box>
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <Box className="flex flex-col p-[1rem] gap-[10px]">
              <Typography>Refund not found</Typography>
            </Box>
          </Box>
        </Box>
      </Layout>
    );
  }

  const formattedDate = formatDateFull(refundDetails.createdAt);

  return (
    <Layout title="Refund Details">
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: 'white',
            paddingRight: '10px',
            flexShrink: 0,
          }}
        >
          <AdminNavbar />
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            padding: '0 1rem',
          }}
        >
          <Box sx={{ paddingTop: '0.75rem', paddingBottom: '0.5rem' }}>
            <IconButton
              size="small"
              onClick={() => navigate('/admin/payments/refund')}
              className="refund-back-icon-button"
            >
              <ArrowSquareLeft size="20" />
            </IconButton>
          </Box>
          <Box className="bg-[#F9F9F9] rounded-lg" sx={{ marginBottom: '1rem' }}>
            <Box className="flex flex-col p-[1rem] gap-[10px]">
              {/* Breadcrumb + header container inside grey card */}
              <Box className="flex flex-col gap-[5px]">
                <Box className="flex items-center gap-2">
                  <Typography
                    className="pointer-text"
                    onClick={() => navigate('/admin/payments/refund')}
                  >
                    Payments {'>'} Refund {'>'} {'>'}
                  </Typography>
                  <Typography className="view-text">View</Typography>
                </Box>

                {/* Header with Refund ID and Status */}
                <div className="refund-details-header">
                  <Box className="refund-details-header-left">
                    <Typography className="refund-details-header-title">
                      Refund ID #{refundDetails.refundId}
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        borderRadius: '20px',
                        border: `1px solid ${StatusColor[refundDetails.status]?.color || '#E0AD14'}`,
                        backgroundColor: StatusColor[refundDetails.status]?.bgcolor || '#FFFCF2',
                        color: StatusColor[refundDetails.status]?.color || '#E0AD14',
                        padding: '2px 8px',
                        maxWidth: 'fit-content',
                      }}
                    >
                      <Box
                        sx={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: StatusColor[refundDetails.status]?.color || '#E0AD14',
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'Poppins',
                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: 'normal',
                          whiteSpace: 'nowrap',
                          textTransform: 'capitalize',
                        }}
                      >
                        {StatusColor[refundDetails.status]?.value || 'Pending'}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    className="refund-more-icon-button"
                    onClick={e => setMenuAnchorEl(e.currentTarget)}
                    size="small"
                  >
                    <More size="20" />
                  </IconButton>
                </div>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={() => setMenuAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    className: 'refund-menu-paper',
                  }}
                >
                  <MenuItem
                    className="refund-menu-item"
                    onClick={() => {
                      setMenuAnchorEl(null);
                      if (refundDetails.paymentId) {
                        navigate(`/admin/payments/transactions-details/${refundDetails.paymentId}`);
                      }
                    }}
                  >
                    View the PayIn
                  </MenuItem>
                </Menu>
                <Typography variant="body2" color="textSecondary">
                  {formattedDate}
                </Typography>
              </Box>

              {/* User Details Section */}
              <div className="refund-section">
                <Box className="refund-section-header">
                  <h3 className="refund-section-title">User Details</h3>
                  <Typography
                    className="refund-view-details-link"
                    onClick={() => {
                      if (refundDetails.userRoleSpecificId) {
                        if (refundDetails.userRole === 'Instructor') {
                          navigate(
                            `/admin/users/instructor-details/${refundDetails.userRoleSpecificId}`
                          );
                        } else {
                          navigate(
                            `/admin/users/learner-details/${refundDetails.userRoleSpecificId}`
                          );
                        }
                      }
                    }}
                  >
                    View Details
                  </Typography>
                </Box>
                <div className="refund-info-grid">
                  <div className="refund-info-item">
                    <span className="refund-info-label">Name</span>
                    <span className="refund-info-value">{refundDetails.userName}</span>
                  </div>
                  <div className="refund-info-item">
                    <span className="refund-info-label">User Role</span>
                    <div
                      className="refund-role-tag"
                      style={{
                        backgroundColor: UserRoleColor[refundDetails.userRole]?.bgcolor,
                        color: UserRoleColor[refundDetails.userRole]?.color,
                      }}
                    >
                      {UserRoleColor[refundDetails.userRole]?.value}
                    </div>
                  </div>
                  <div className="refund-info-item">
                    <span className="refund-info-label">Phone Number</span>
                    <span className="refund-info-value">{refundDetails.phoneNumber}</span>
                  </div>
                  <div className="refund-info-item">
                    <span className="refund-info-label">Email</span>
                    <span className="refund-info-value">{refundDetails.email}</span>
                  </div>
                  <div className="refund-info-item">
                    <span className="refund-info-label">Current Location</span>
                    <span className="refund-info-value">{refundDetails.currentLocation}</span>
                  </div>
                  <div className="refund-info-item">
                    <span className="refund-info-label">Date of Joining</span>
                    <span className="refund-info-value">
                      {refundDetails.dateOfJoining
                        ? formatDateFull(refundDetails.dateOfJoining)
                        : '-'}
                    </span>
                  </div>
                  <div className="refund-info-item refund-reason-full">
                    <span className="refund-info-label">Refund Reason</span>
                    <span className="refund-info-value">{refundDetails.refundReason}</span>
                  </div>
                  {refundDetails.status === 'rejected' && refundDetails.rejectionReason && (
                    <div className="refund-info-item refund-reason-full">
                      <span className="refund-info-label">Rejection Reason</span>
                      <span className="refund-info-value" style={{ color: '#E14949' }}>
                        {refundDetails.rejectionReason}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Gateway Details Section */}
              <div className="refund-section">
                <Box className="refund-section-header">
                  <h3 className="refund-section-title">Payment Gateway Details</h3>
                </Box>
                <div className="refund-info-grid">
                  <div className="refund-info-item">
                    <span className="refund-info-label">Payment Method</span>
                    <span className="refund-info-value">{refundDetails.paymentMethod}</span>
                  </div>
                  <div className="refund-info-item">
                    <span className="refund-info-label">Payment ID</span>
                    <span className="refund-info-value">{refundDetails.paymentId}</span>
                  </div>
                  <div className="refund-info-item">
                    <span className="refund-info-label">Payment Status</span>
                    <span className="refund-info-value">{refundDetails.paymentStatus}</span>
                  </div>
                  <div className="refund-info-item">
                    <span className="refund-info-label">Timestamp</span>
                    <span className="refund-info-value">
                      {refundDetails.timestamp ? formatDateFull(refundDetails.timestamp) : '-'}
                    </span>
                  </div>

                  {refundDetails.paymentMethodRaw?.toLowerCase() === 'apple_pay' && (
                    <>
                      <div className="refund-info-item">
                        <span className="refund-info-label">Apple Pay Reference</span>
                        <span className="refund-info-value">
                          {refundDetails.gatewayFields?.referenceNumber}
                        </span>
                      </div>
                      <div className="refund-info-item">
                        <span className="refund-info-label">Order Number</span>
                        <span className="refund-info-value">
                          {refundDetails.gatewayFields?.confirmationId}
                        </span>
                      </div>
                      {refundDetails.gatewayFields?.additionalRef && (
                        <div className="refund-info-item">
                          <span className="refund-info-label">QPay Reference</span>
                          <span className="refund-info-value">
                            {refundDetails.gatewayFields.additionalRef}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {(refundDetails.paymentMethodRaw?.toLowerCase() === 'qpay' ||
                    refundDetails.paymentMethodRaw?.toLowerCase() === 'qpay_ez_connect') && (
                    <>
                      <div className="refund-info-item">
                        <span className="refund-info-label">QPay Reference Number</span>
                        <span className="refund-info-value">
                          {refundDetails.gatewayFields?.referenceNumber}
                        </span>
                      </div>
                      <div className="refund-info-item">
                        <span className="refund-info-label">Confirmation ID</span>
                        <span className="refund-info-value">
                          {refundDetails.gatewayFields?.confirmationId}
                        </span>
                      </div>
                    </>
                  )}

                  {(refundDetails.paymentMethodRaw?.toLowerCase() === 'cybs' ||
                    refundDetails.paymentMethodRaw?.toLowerCase() === 'cybersource' ||
                    refundDetails.paymentMethodRaw?.toLowerCase() === 'cybersource_card') && (
                    <div className="refund-info-item">
                      <span className="refund-info-label">Transaction ID</span>
                      <span className="refund-info-value">
                        {refundDetails.gatewayFields?.transactionId}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Details Section */}
              <div className="refund-section">
                <Box className="refund-section-header">
                  <h3 className="refund-section-title">Payment Details</h3>
                  <Typography
                    className="refund-view-details-link"
                    onClick={() => {
                      console.log('🔍 [RefundDetails] View All Refunds clicked');
                      console.log('🔍 [RefundDetails] studentId:', refundDetails.studentId);
                      if (refundDetails.studentId) {
                        const url = `/admin/payments/refund?student_id=${refundDetails.studentId}`;
                        console.log('🔍 [RefundDetails] Navigating to:', url);
                        navigate(url);
                      } else {
                        console.error('❌ [RefundDetails] studentId is missing!');
                      }
                    }}
                  >
                    View All Refunds
                  </Typography>
                </Box>
                <div className="refund-payment-summary">
                  <div className="refund-payment-row">
                    <span className="refund-payment-label">Purchased</span>
                    <span className="refund-payment-value">
                      {refundDetails.purchasedKoins || 0} Koins (
                      {refundDetails.purchasedAmount || 0} Riyal)
                    </span>
                  </div>
                  <div className="refund-payment-row">
                    <span className="refund-payment-label">Consumed</span>
                    <span className="refund-payment-value refund-consumed-value">
                      {refundDetails.consumedKoins || 0} Koins ({refundDetails.consumedAmount || 0}{' '}
                      Riyal)
                    </span>
                  </div>
                  <div className="refund-payment-row">
                    <span className="refund-payment-label">Refund Available</span>
                    <span className="refund-payment-value refund-available-value">
                      {refundDetails.refundKoins || 0} Koins
                    </span>
                  </div>
                  <div className="refund-payment-total-row">
                    <span className="refund-total-label">Refund Total</span>
                    <span className="refund-total-value">{refundDetails.refundAmount} Riyal</span>
                  </div>
                </div>
              </div>

              {/* Processed By Section - Only show when approved/completed/rejected/processing */}
              {(refundDetails.status === 'approved' ||
                refundDetails.status === 'completed' ||
                refundDetails.status === 'rejected' ||
                refundDetails.status === 'processing') &&
                refundDetails.processedBy && (
                  <Box
                    className="refund-processed-by-section"
                    sx={{
                      border: refundDetails.status === 'rejected' ? '1px solid #E14949' : 'none',
                      background: refundDetails.status === 'rejected' ? '#FFF2F2' : '#f3f7ff',
                    }}
                  >
                    <Box className="refund-processed-by-header">
                      <Typography className="refund-processed-by-title">
                        {refundDetails.status === 'rejected' ? 'Rejected By' : 'Processed By'}
                      </Typography>
                      <Box
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
                      >
                        <Typography className="refund-processed-by-name">
                          {formatProcessedByNameWithRole(
                            refundDetails.processedBy,
                            refundDetails.processedByRole
                          )}
                        </Typography>
                        {refundDetails.processedAt && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontSize: '12px', marginTop: '2px' }}
                          >
                            {formatDateTime(refundDetails.processedAt, 'DD MMM YYYY, hh:mm:ss A')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {refundDetails.adminNotes && (
                      <Box className="refund-posted-note-container">
                        <Typography className="refund-posted-note-text">
                          {refundDetails.adminNotes}
                        </Typography>
                      </Box>
                    )}
                    {!refundDetails.adminNotes && (
                      <Box className="refund-note-container">
                        <TextField
                          className="refund-note-input"
                          placeholder="Add a note..."
                          multiline
                          rows={3}
                          value={note}
                          onChange={e => setNote(e.target.value)}
                          variant="outlined"
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              border: 'none',
                              padding: 0,
                              '& fieldset': {
                                border: 'none',
                              },
                              '&:hover fieldset': {
                                border: 'none',
                              },
                              '&.Mui-focused fieldset': {
                                border: 'none',
                              },
                            },
                          }}
                        />
                        <Button
                          className="refund-post-button"
                          onClick={async () => {
                            if (note.trim()) {
                              try {
                                await axiosInstance.patch(
                                  `/admin/students/refund-requests/${id}/admin-notes`,
                                  {
                                    admin_notes: note,
                                  }
                                );
                                toast.success('Note posted successfully');
                                setNote('');
                                fetchRefundDetails();
                              } catch (error) {
                                toast.error('Failed to post note');
                              }
                            }
                          }}
                          disabled={!note.trim()}
                        >
                          Post
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

              {/* Action Buttons */}
              {refundDetails.status === 'pending' && (
                <Box className="refund-action-buttons">
                  <Button
                    variant="outlined"
                    className="refund-reject-btn"
                    onClick={() => setRejectModalOpen(true)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : 'Reject'}
                  </Button>
                  <Button
                    variant="contained"
                    className="refund-approve-btn"
                    onClick={() => setApproveModalOpen(true)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : 'Approve'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Modals */}
      {approveModalOpen && (
        <RefundApproveModal setApproveOpen={setApproveModalOpen} onApprove={handleApprove} />
      )}
      {rejectModalOpen && (
        <RefundRejectModal setRejectOpen={setRejectModalOpen} onReject={handleReject} />
      )}
    </Layout>
  );
};

export default RefundDetails;
